/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { useEffect, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ScreenType } from 'client-code/previews';

import { DEFAULT_RETRY_OPTIONS } from '../../../../config';
import { ServicesClientImpl } from '../../../../core/interactors';
import { Locale } from '../../../../core/locale/Locale';
import { LocaleKeys } from '../../../../core/locale/LocaleKeys';
import { Log } from '../../../../core/logging';
import { RetryMechanism } from '../../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../../core/retrymechanism/RetryOptions';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { TOfferPreview } from '../../../../core/types';
import {
    setOneLineGuideCurrentOfferPreviewAction,
    setOneLineGuideCurrentSelectedCellStarRatingAction,
} from '../../../../store/videoplayerui/onelineguide/onelineguide.actions';
import { PreviewDescriptionOneLineGuide } from '../PreviewDescriptionOneLineGuide';
import ShowInfoOneLineGuide from '../ShowInfoOneLineGuide/ShowInfoOneLineGuide';
import { ShowInfoOneLineGuideProps } from '../ShowInfoOneLineGuide/ShowInfoOneLineGuideProps';

import './PreviewOneLineGuide.scss';

const TAG = 'PreviewOneLineGuide';

const services = new ServicesClientImpl();
const DEBOUNCE_VALUE = 500; // ms

const PreviewOneLineGuide: React.FC = (): JSX.Element => {
    const { currentSelectedCell, currentAiringCell, selectedChannel, isFutureMode } =
        useTivoSelector((state: RootState) => state.oneLineGuide);

    const [cache, setCache] = useState({});

    const dispatch = useTivoDispatch();

    const currentOfferId = currentSelectedCell?.offerId;
    const preview = cache[currentOfferId]?.offerPreview;

    useEffect(() => setCache({}), [selectedChannel]);

    useEffect(() => {
        const previewForOffer = cache[currentOfferId]?.offerPreview;
        const starRating = previewForOffer?.contentPreview?.starRating || null;
        let timeoutId: NodeJS.Timeout;
        dispatch(setOneLineGuideCurrentSelectedCellStarRatingAction(starRating));
        if (!previewForOffer) {
            timeoutId = setTimeout(() => {
                const fetchOfferPreview = services.fetchOfferPreview.bind(services);
                const previewPromise = RetryMechanism.callServiceWithRetry<TOfferPreview>(
                    fetchOfferPreview,
                    [currentOfferId, ScreenType.GuideHeader],
                    DEFAULT_RETRY_OPTIONS as RetryOptions,
                );

                dispatch(setOneLineGuideCurrentOfferPreviewAction(previewPromise));

                previewPromise
                    .then((previewResponse: TOfferPreview) => {
                        setCache((prevCache) => ({
                            ...prevCache,
                            [currentOfferId]: previewResponse,
                        }));
                    })
                    .catch((error) => {
                        Diagnostics.error(
                            TAG,
                            'Error while fetching Offer Preview Failed to get guide OneLineGuide.getPreview() -> services.fetchOfferPreview',
                        );
                        Diagnostics.error(TAG, 'stacktrace:', error);
                        Log.e(TAG, error);
                    });
            }, DEBOUNCE_VALUE);
        } else {
            dispatch(
                setOneLineGuideCurrentOfferPreviewAction(
                    Promise.resolve({ offerPreview: previewForOffer }),
                ),
            );
        }

        // cleanup function;
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [cache, currentOfferId, dispatch]);

    let description = '';

    if (preview?.contentPreview) {
        if (preview.contentPreview.isAdult && services.shouldHideMetadata()) {
            description = Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN);
        } else {
            description =
                preview.contentPreview.description ||
                Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);
        }
    }

    const details =
        !preview?.contentPreview?.isAdult || !services.shouldHideMetadata()
            ? ` (${[
                  preview?.hasCc ? 'CC' : '',
                  preview?.videoResolution?.toUpperCase() || '',
                  preview?.isRepeat ? 'R' : '',
                  preview?.hasSap ? 'SAP' : '',
              ]
                  .filter(Boolean)
                  .join(', ')})`
            : '';

    const showInfo: ShowInfoOneLineGuideProps = {
        duration: currentSelectedCell?.duration,
        isFutureMode,
        recordingStatus: preview?.contentPreview?.recordingStatus,
        isNew: currentSelectedCell?.isNew,
        isLive: currentSelectedCell === currentAiringCell,
        providersImageUrls: preview?.contentPreview?.providersImageUrls,
        startTime: currentSelectedCell?.startTime,
    };

    return (
        <div className="olg-preview-container">
            {preview && (
                <>
                    <ShowInfoOneLineGuide {...showInfo} />
                    <PreviewDescriptionOneLineGuide description={description} details={details} />
                </>
            )}
        </div>
    );
};

export default PreviewOneLineGuide;
